<template><div><component-combinator component-name="DtInput" /><h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">This component combines both the <code v-pre>input</code> and <code v-pre>textarea</code> elements as options within a single component. Its default presentation includes a paired text <code v-pre>label</code>.</p>
<dialtone-usage>
<template #do>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">If you can't reasonably predict a user's answer to a prompt and there might be wide variability in users' answers.</li>
<li class="d-docsite--list-element">When using another type of input will make answering more difficult. For example, birthdays and other known dates are easier to type in than they are to select from a calendar picker.</li>
<li class="d-docsite--list-element">When users want to be able to paste in a response.</li>
</ul>
</template>
<template #dont>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">When users are choosing from a specific set of options. Consider <RouteLink to="/components/select-menu.html" class="d-docsite--link d-link">Select</RouteLink>, <RouteLink to="/components/radio.html" class="d-docsite--link d-link">Radio</RouteLink>, or <RouteLink to="/components/checkbox.html" class="d-docsite--link d-link">Checkbox</RouteLink>.</li>
</ul>
</template>
</dialtone-usage>
<h3 id="best-practices" tabindex="-1" class="d-docsite--header-3">Best Practices <a class="header-anchor d-link d-docsite--link d-link" href="#best-practices" aria-hidden="true">#</a></h3>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">The length of the text input provides a hint to users as to how much text to enter.</li>
<li class="d-docsite--list-element">Only show error validation messages or stylings after a user has interacted with a particular field.</li>
<li class="d-docsite--list-element">Do not use placeholder text (i.e. <code v-pre>placeholder</code> attribute) in place of an accessible <code v-pre>label</code>.</li>
<li class="d-docsite--list-element">Consider the type of content a user may enter to aid mobile device entry; mobile devices typically surface a keyboard UI attuned to the type. For example, type=&quot;tel&quot; will surface a <a href="http://html5doctor.com/html5-forms-input-types/#input-tel" class="d-docsite--link d-link" target="_blank" rel="noopener noreferrer">phone keyboard</a>.</li>
</ul>
<h2 id="sizes" tabindex="-1" class="d-docsite--header-2">Sizes <a class="header-anchor d-link d-docsite--link d-link" href="#sizes" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">We offer different sizes for instances in which the interface requires a smaller or larger input. In general, though, use the base <code v-pre>300</code> (medium) size input as much as possible, especially in forms.</p>
<code-example source-code='&lt;dt-input :size=&quot;100&quot; type=&quot;text&quot; label=&quot;Extra Small&quot; placeholder=&quot;Placeholder&quot; /&gt;
&lt;dt-input :size=&quot;100&quot; type=&quot;textarea&quot; label=&quot;Extra Small&quot; placeholder=&quot;Placeholder&quot; /&gt;
&lt;dt-input :size=&quot;200&quot; type=&quot;text&quot; label=&quot;Small&quot; placeholder=&quot;Placeholder&quot; /&gt;
&lt;dt-input :size=&quot;200&quot; type=&quot;textarea&quot; label=&quot;Small&quot; placeholder=&quot;Placeholder&quot; /&gt;
&lt;dt-input :size=&quot;300&quot; type=&quot;text&quot; label=&quot;Medium&quot; placeholder=&quot;Placeholder&quot; /&gt;
&lt;dt-input :size=&quot;300&quot; type=&quot;textarea&quot; label=&quot;Medium&quot; placeholder=&quot;Placeholder&quot; /&gt;
&lt;dt-input :size=&quot;400&quot; type=&quot;text&quot; label=&quot;Large&quot; placeholder=&quot;Placeholder&quot; /&gt;
&lt;dt-input :size=&quot;400&quot; type=&quot;textarea&quot; label=&quot;Large&quot; placeholder=&quot;Placeholder&quot; /&gt;
&lt;dt-input :size=&quot;500&quot; type=&quot;text&quot; label=&quot;Extra large&quot; placeholder=&quot;Placeholder&quot; /&gt;
&lt;dt-input :size=&quot;500&quot; type=&quot;textarea&quot; label=&quot;Extra large&quot; placeholder=&quot;Placeholder&quot; /&gt;'>
<div data-demo-wrapper class="d-d-grid d-g-200 d-g-cols2">
  <dt-input :size="100" type="text" label="Extra Small" placeholder="Placeholder" />
  <dt-input :size="100" type="textarea" label="Extra Small" placeholder="Placeholder" />
  <dt-input :size="200" type="text" label="Small" placeholder="Placeholder" />
  <dt-input :size="200" type="textarea" label="Small" placeholder="Placeholder" />
  <dt-input :size="300" type="text" label="Medium" placeholder="Placeholder" />
  <dt-input :size="300" type="textarea" label="Medium" placeholder="Placeholder" />
  <dt-input :size="400" type="text" label="Large" placeholder="Placeholder" />
  <dt-input :size="400" type="textarea" label="Large" placeholder="Placeholder" />
  <dt-input :size="500" type="text" label="Extra large" placeholder="Placeholder" />
  <dt-input :size="500" type="textarea" label="Extra large" placeholder="Placeholder" />
</div>
</code-example>
<h2 id="examples" tabindex="-1" class="d-docsite--header-2">Examples <a class="header-anchor d-link d-docsite--link d-link" href="#examples" aria-hidden="true">#</a></h2>
<h3 id="base-styles" tabindex="-1" class="d-docsite--header-3">Base Styles <a class="header-anchor d-link d-docsite--link d-link" href="#base-styles" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">An input is normally paired with a label, but there are times when it can be used without a label.  Placeholder text should primarily be used as a content prompt and only provided when needed.</p>
<code-example source-code='&lt;dt-input label=&quot;Label&quot; placeholder=&quot;Placeholder&quot; /&gt;
&lt;dt-input label=&quot;Label&quot; model-value=&quot;Value&quot; /&gt;
&lt;dt-input label=&quot;Label&quot; placeholder=&quot;Placeholder&quot; disabled /&gt;
&lt;dt-input label=&quot;Label&quot; placeholder=&quot;Placeholder&quot; type=&quot;textarea&quot; /&gt;
&lt;dt-input label=&quot;Label&quot; type=&quot;textarea&quot; model-value=&quot;Value&quot; /&gt;
&lt;dt-input label=&quot;Label&quot; placeholder=&quot;Placeholder&quot; type=&quot;textarea&quot; disabled /&gt;'>
<div data-demo-wrapper class="d-d-grid d-g-200 d-g-cols3">
  <dt-input label="Label" placeholder="Placeholder" />
  <dt-input label="Label" model-value="Value" />
  <dt-input label="Label" placeholder="Placeholder" disabled />
  <dt-input label="Label" placeholder="Placeholder" type="textarea" />
  <dt-input label="Label" type="textarea" model-value="Value" />
  <dt-input label="Label" placeholder="Placeholder" type="textarea" disabled />
</div>
</code-example>
<h3 id="with-description-text" tabindex="-1" class="d-docsite--header-3">With Description Text <a class="header-anchor d-link d-docsite--link d-link" href="#with-description-text" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-input label=&quot;Label&quot; description=&quot;Helpful description text&quot; placeholder=&quot;Placeholder&quot;/&gt;
&lt;dt-input label=&quot;Label&quot; description=&quot;Helpful description text&quot; type=&quot;textarea&quot; placeholder=&quot;Placeholder&quot;/&gt;'>
<div data-demo-wrapper class="d-d-grid d-g-200 d-g-cols2">
  <dt-input label="Label" description="Helpful description text" placeholder="Placeholder"/>
  <dt-input label="Label" description="Helpful description text" type="textarea" placeholder="Placeholder"/>
</div>
</code-example>
<h3 id="with-validation-states" tabindex="-1" class="d-docsite--header-3">With Validation States <a class="header-anchor d-link d-docsite--link d-link" href="#with-validation-states" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Provides feedback to the user based on their interaction, or lack thereof, with an input.</p>
<code-example source-code='&lt;dt-input label=&quot;Label&quot; type=&quot;email&quot; model-value=&quot;Value&quot; :messages=&quot;[messages.critical]&quot;/&gt;
&lt;dt-input label=&quot;Label&quot; type=&quot;email&quot; model-value=&quot;Value&quot; :messages=&quot;[messages.positive]&quot;/&gt;
&lt;dt-input label=&quot;Label&quot; type=&quot;email&quot; model-value=&quot;Value&quot; :messages=&quot;[messages.warning]&quot;/&gt;
&lt;dt-input label=&quot;Label&quot; type=&quot;textarea&quot; model-value=&quot;Value&quot; :messages=&quot;[messages.critical]&quot;/&gt;
&lt;dt-input label=&quot;Label&quot; type=&quot;textarea&quot; model-value=&quot;Value&quot; :messages=&quot;[messages.positive]&quot;/&gt;
&lt;dt-input label=&quot;Label&quot; type=&quot;textarea&quot; model-value=&quot;Value&quot; :messages=&quot;[messages.warning]&quot;/&gt;'>
<div data-demo-wrapper class="d-d-grid d-g-200 d-g-cols3">
  <dt-input label="Label" type="email" model-value="Value" :messages="[messages.critical]"/>
  <dt-input label="Label" type="email" model-value="Value" :messages="[messages.positive]"/>
  <dt-input label="Label" type="email" model-value="Value" :messages="[messages.warning]"/>
  <dt-input label="Label" type="textarea" model-value="Value" :messages="[messages.critical]"/>
  <dt-input label="Label" type="textarea" model-value="Value" :messages="[messages.positive]"/>
  <dt-input label="Label" type="textarea" model-value="Value" :messages="[messages.warning]"/>
</div>
</code-example>
<h3 id="with-multiple-validation-messages" tabindex="-1" class="d-docsite--header-3">With Multiple Validation Messages <a class="header-anchor d-link d-docsite--link d-link" href="#with-multiple-validation-messages" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-input label=&quot;Label&quot; type=&quot;email&quot; model-value=&quot;Value&quot; :messages=&quot;multipleMessages&quot; /&gt;'>
<dt-input label="Label" type="email" model-value="Value" :messages="multipleMessages" />
</code-example>
<h3 id="with-maximum-length-validation" tabindex="-1" class="d-docsite--header-3">With Maximum Length Validation <a class="header-anchor d-link d-docsite--link d-link" href="#with-maximum-length-validation" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Adds validation for the input length. Make sure to provide the following props:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element"><code v-pre>currentLength</code>: the current character length that the user has entered into the input. This must be input manually as sometimes characters do not count as 1 character. For example an emoji could take up many characters in the input, but should only count as 1 character. If you don't pass <code v-pre>currentLength</code>, the component will use a built-in length calculation.</li>
<li class="d-docsite--list-element"><code v-pre>validate</code>: should be an object with the validation rules to apply to the input. Maximum length validation is supported with the following configuration:</li>
</ul>
<div class="language-javascript" data-highlighter="prismjs" data-ext="js"><pre v-pre><code class="language-javascript"><span class="line"><span class="token literal-property property">length</span><span class="token operator">:</span> <span class="token punctuation">{</span></span>
<span class="line">  <span class="token comment">// describes the maximum length allowed and shown in the label</span></span>
<span class="line">  <span class="token literal-property property">description</span><span class="token operator">:</span> string<span class="token punctuation">,</span>        <span class="token comment">// Required</span></span>
<span class="line">  <span class="token comment">// maximum length allowed to enter</span></span>
<span class="line">  <span class="token literal-property property">max</span><span class="token operator">:</span> number<span class="token punctuation">,</span>                <span class="token comment">// Required</span></span>
<span class="line">  <span class="token comment">// message to show in the warning or error validation message</span></span>
<span class="line">  <span class="token literal-property property">message</span><span class="token operator">:</span> string<span class="token punctuation">,</span>            <span class="token comment">// Required</span></span>
<span class="line">  <span class="token comment">// length from which the validation message will be shown as a warning,</span></span>
<span class="line">  <span class="token comment">// when the maximum length is reached, the validation message will be shown as an error</span></span>
<span class="line">  <span class="token literal-property property">warn</span><span class="token operator">:</span> number<span class="token punctuation">,</span>               <span class="token comment">// Optional</span></span>
<span class="line">  <span class="token comment">// set maxlength attribute, defaults to false</span></span>
<span class="line">  <span class="token literal-property property">limitMaxLength</span><span class="token operator">:</span> boolean<span class="token punctuation">,</span>    <span class="token comment">// Optional</span></span>
<span class="line"><span class="token punctuation">}</span><span class="token punctuation">,</span></span>
<span class="line"></span></code></pre>
</div><p class="d-docsite--paragraph">If the input is invalid due to the validation, the validation message will be shown even when the input lost focus, otherwise the validation message will be hidden when the user unfocuses the input.</p>
<code-example source-code='&lt;dt-input
  model-value=&quot;Value&quot;
  label=&quot;Label&quot;
  placeholder=&quot;placeholder&quot;
  :validate=&quot;{
    length: {
      description: &#39;Max 25 characters.&#39;,
      message: &#39;Max 25 characters allowed.&#39;,
      max: 25,
      warn: 15,
      limitMaxLength: false,
    }
  }&quot;
/&gt;'>
<dt-input
  model-value="Value"
  label="Label"
  placeholder="placeholder"
  :validate="{
    length: {
      description: 'Max 25 characters.',
      message: 'Max 25 characters allowed.',
      max: 25,
      warn: 15,
      limitMaxLength: false,
    }
  }"
/>
</code-example>
<h3 id="with-custom-maximum-length-validation-message" tabindex="-1" class="d-docsite--header-3">With Custom Maximum Length Validation Message <a class="header-anchor d-link d-docsite--link d-link" href="#with-custom-maximum-length-validation-message" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-input
  model-value=&quot;Value&quot;
  label=&quot;Label&quot;
  placeholder=&quot;placeholder&quot;
  :validate=&quot;validate()&quot;
/&gt;'>
<dt-input
  model-value="Value"
  label="Label"
  placeholder="placeholder"
  :validate="validate()"
/>
</code-example>
<div class="language-javascript" data-highlighter="prismjs" data-ext="js"><pre v-pre><code class="language-javascript"><span class="line"><span class="token keyword">const</span> validateData <span class="token operator">=</span> <span class="token punctuation">{</span></span>
<span class="line">  <span class="token literal-property property">length</span><span class="token operator">:</span> <span class="token punctuation">{</span></span>
<span class="line">    <span class="token literal-property property">description</span><span class="token operator">:</span> <span class="token string">'Max 25 characters.'</span><span class="token punctuation">,</span></span>
<span class="line">    <span class="token literal-property property">max</span><span class="token operator">:</span> <span class="token number">25</span><span class="token punctuation">,</span></span>
<span class="line">    <span class="token literal-property property">warn</span><span class="token operator">:</span> <span class="token number">15</span><span class="token punctuation">,</span></span>
<span class="line">    <span class="token literal-property property">limitMaxLength</span><span class="token operator">:</span> <span class="token boolean">false</span><span class="token punctuation">,</span></span>
<span class="line">  <span class="token punctuation">}</span></span>
<span class="line"><span class="token punctuation">}</span><span class="token punctuation">;</span></span>
<span class="line"></span>
<span class="line"><span class="token keyword">const</span> <span class="token function-variable function">validationMessage</span> <span class="token operator">=</span> <span class="token punctuation">(</span><span class="token punctuation">)</span> <span class="token operator">=></span> <span class="token punctuation">{</span></span>
<span class="line">  <span class="token keyword">const</span> remainingCharacters <span class="token operator">=</span> validateData<span class="token punctuation">.</span>length<span class="token punctuation">.</span>max <span class="token operator">-</span> currentLength<span class="token punctuation">.</span>value<span class="token punctuation">.</span>length<span class="token punctuation">;</span></span>
<span class="line"></span>
<span class="line">  <span class="token keyword">if</span> <span class="token punctuation">(</span>remainingCharacters <span class="token operator">&lt;</span> <span class="token number">0</span><span class="token punctuation">)</span> <span class="token punctuation">{</span></span>
<span class="line">    <span class="token keyword">return</span> <span class="token template-string"><span class="token template-punctuation string">`</span><span class="token interpolation"><span class="token interpolation-punctuation punctuation">${</span>Math<span class="token punctuation">.</span><span class="token function">abs</span><span class="token punctuation">(</span>remainingCharacters<span class="token punctuation">)</span><span class="token interpolation-punctuation punctuation">}</span></span><span class="token string"> characters over limit</span><span class="token template-punctuation string">`</span></span><span class="token punctuation">;</span></span>
<span class="line">  <span class="token punctuation">}</span> <span class="token keyword">else</span> <span class="token punctuation">{</span></span>
<span class="line">    <span class="token keyword">return</span> <span class="token template-string"><span class="token template-punctuation string">`</span><span class="token interpolation"><span class="token interpolation-punctuation punctuation">${</span>remainingCharacters<span class="token interpolation-punctuation punctuation">}</span></span><span class="token string"> characters left</span><span class="token template-punctuation string">`</span></span><span class="token punctuation">;</span></span>
<span class="line">  <span class="token punctuation">}</span></span>
<span class="line"><span class="token punctuation">}</span><span class="token punctuation">;</span></span>
<span class="line"></span>
<span class="line"><span class="token keyword">const</span> <span class="token function-variable function">validate</span> <span class="token operator">=</span> <span class="token punctuation">(</span><span class="token punctuation">)</span> <span class="token operator">=></span> <span class="token punctuation">{</span></span>
<span class="line">  <span class="token keyword">return</span> <span class="token punctuation">{</span></span>
<span class="line">    <span class="token literal-property property">length</span><span class="token operator">:</span> <span class="token punctuation">{</span></span>
<span class="line">      <span class="token operator">...</span>validateData<span class="token punctuation">.</span>length<span class="token punctuation">,</span></span>
<span class="line">      <span class="token literal-property property">message</span><span class="token operator">:</span> <span class="token function">validationMessage</span><span class="token punctuation">(</span><span class="token punctuation">)</span><span class="token punctuation">,</span></span>
<span class="line">    <span class="token punctuation">}</span></span>
<span class="line">  <span class="token punctuation">}</span><span class="token punctuation">;</span></span>
<span class="line"><span class="token punctuation">}</span><span class="token punctuation">;</span></span>
<span class="line"></span></code></pre>
</div><h3 id="search" tabindex="-1" class="d-docsite--header-3">Search <a class="header-anchor d-link d-docsite--link d-link" href="#search" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Use <code v-pre>type=&quot;search&quot;</code> with a clear button in the <code v-pre>icon</code> slot. When the input is not empty, the clear button will render and will clear the input field when triggered.</p>
<code-example source-code='&lt;dt-input
  aria-label=&quot;Search items&quot;
  placeholder=&quot;Search Items&quot;
  type=&quot;search&quot;
  model-value=&quot;Search Value&quot;
&gt;
  &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
    &lt;dt-icon name=&quot;search&quot; :size=&quot;iconSize&quot; /&gt;
  &lt;/template&gt;
  &lt;template v-if=&quot;inputSearchValue.length !== 0&quot; #endIcon=&quot;{ clear }&quot;&gt;
    &lt;dt-stack class=&quot;d-pie-25&quot;&gt;
      &lt;dt-button
        v-dt-tooltip=&quot;&#39;Clear search&#39;&quot;
        kind=&quot;muted&quot;
        importance=&quot;clear&quot;
        :size=&quot;100&quot;
        aria-label=&quot;Clear search&quot;
        @click=&quot;clear&quot;
      &gt;
        &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
          &lt;dt-icon name=&quot;close&quot; :size=&quot;iconSize&quot; /&gt;
        &lt;/template&gt;
      &lt;/dt-button&gt;
    &lt;/dt-stack&gt;
  &lt;/template&gt;
&lt;/dt-input&gt;'>
<dt-input
  aria-label="Search items"
  placeholder="Search Items"
  type="search"
  model-value="Search Value"
>
  <template #startIcon="{ iconSize }">
    <dt-icon name="search" :size="iconSize" />
  </template>
  <template v-if="inputSearchValue.length !== 0" #endIcon="{ clear }">
    <dt-stack class="d-pie-25">
      <dt-button
        v-dt-tooltip="'Clear search'"
        kind="muted"
        importance="clear"
        :size="100"
        aria-label="Clear search"
        @click="clear"
      >
        <template #startIcon="{ iconSize }">
          <dt-icon name="close" :size="iconSize" />
        </template>
      </dt-button>
    </dt-stack>
  </template>
</dt-input>
</code-example>
<h2 id="icon-support" tabindex="-1" class="d-docsite--header-2">Icon Support <a class="header-anchor d-link d-docsite--link d-link" href="#icon-support" aria-hidden="true">#</a></h2>
<code-example source-code='&lt;dt-input label=&quot;Start icon&quot; type=&quot;text&quot; placeholder=&quot;Placeholder&quot;&gt;
  &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
    &lt;dt-icon name=&quot;send&quot; :size=&quot;iconSize&quot; /&gt;
  &lt;/template&gt;
&lt;/dt-input&gt;
&lt;dt-input label=&quot;End icon&quot; type=&quot;text&quot; placeholder=&quot;Placeholder&quot;&gt;
  &lt;template #endIcon=&quot;{ iconSize }&quot;&gt;
    &lt;dt-icon name=&quot;lock&quot; :size=&quot;iconSize&quot; /&gt;
  &lt;/template&gt;
&lt;/dt-input&gt;
&lt;dt-input label=&quot;Start and End icon&quot; type=&quot;text&quot; placeholder=&quot;Placeholder&quot;&gt;
  &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
    &lt;dt-icon name=&quot;send&quot; :size=&quot;iconSize&quot; /&gt;
  &lt;/template&gt;
  &lt;template #endIcon=&quot;{ iconSize }&quot;&gt;
    &lt;dt-icon name=&quot;lock&quot; :size=&quot;iconSize&quot; /&gt;
  &lt;/template&gt;
&lt;/dt-input&gt;
&lt;dt-input label=&quot;Start icon&quot; type=&quot;textarea&quot; placeholder=&quot;Placeholder&quot;&gt;
  &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
    &lt;dt-icon name=&quot;send&quot; :size=&quot;iconSize&quot; /&gt;
  &lt;/template&gt;
&lt;/dt-input&gt;
&lt;dt-input label=&quot;End icon&quot; type=&quot;textarea&quot; placeholder=&quot;Placeholder&quot;&gt;
  &lt;template #endIcon=&quot;{ iconSize }&quot;&gt;
    &lt;dt-icon name=&quot;lock&quot; :size=&quot;iconSize&quot; /&gt;
  &lt;/template&gt;
&lt;/dt-input&gt;
&lt;dt-input label=&quot;Start and End icon&quot; type=&quot;textarea&quot; placeholder=&quot;Placeholder&quot;&gt;
  &lt;template #startIcon=&quot;{ iconSize }&quot;&gt;
    &lt;dt-icon name=&quot;send&quot; :size=&quot;iconSize&quot; /&gt;
  &lt;/template&gt;
  &lt;template #endIcon=&quot;{ iconSize }&quot;&gt;
    &lt;dt-icon name=&quot;lock&quot; :size=&quot;iconSize&quot; /&gt;
  &lt;/template&gt;
&lt;/dt-input&gt;'>
<div data-demo-wrapper class="d-d-grid d-g-200 d-g-cols3">
  <dt-input label="Start icon" type="text" placeholder="Placeholder">
    <template #startIcon="{ iconSize }">
      <dt-icon name="send" :size="iconSize" />
    </template>
  </dt-input>
  <dt-input label="End icon" type="text" placeholder="Placeholder">
    <template #endIcon="{ iconSize }">
      <dt-icon name="lock" :size="iconSize" />
    </template>
  </dt-input>
  <dt-input label="Start and End icon" type="text" placeholder="Placeholder">
    <template #startIcon="{ iconSize }">
      <dt-icon name="send" :size="iconSize" />
    </template>
    <template #endIcon="{ iconSize }">
      <dt-icon name="lock" :size="iconSize" />
    </template>
  </dt-input>
  <dt-input label="Start icon" type="textarea" placeholder="Placeholder">
    <template #startIcon="{ iconSize }">
      <dt-icon name="send" :size="iconSize" />
    </template>
  </dt-input>
  <dt-input label="End icon" type="textarea" placeholder="Placeholder">
    <template #endIcon="{ iconSize }">
      <dt-icon name="lock" :size="iconSize" />
    </template>
  </dt-input>
  <dt-input label="Start and End icon" type="textarea" placeholder="Placeholder">
    <template #startIcon="{ iconSize }">
      <dt-icon name="send" :size="iconSize" />
    </template>
    <template #endIcon="{ iconSize }">
      <dt-icon name="lock" :size="iconSize" />
    </template>
  </dt-input>
</div>
</code-example>
<h3 id="icon-sizes" tabindex="-1" class="d-docsite--header-3">Icon Sizes <a class="header-anchor d-link d-docsite--link d-link" href="#icon-sizes" aria-hidden="true">#</a></h3>
<p class="d-docsite--paragraph">Each Text Input size has a default icon size, keeping it proportional. While rare, customizing the icon size is possible.</p>
<code-example source-code='&lt;dt-input label=&quot;Medium input with smallest icon&quot; type=&quot;text&quot; placeholder=&quot;Placeholder&quot; :size=&quot;300&quot;&gt;
  &lt;template #startIcon&gt;
    &lt;dt-icon name=&quot;box-select&quot; size=&quot;100&quot; /&gt;
  &lt;/template&gt;
  &lt;template #endIcon&gt;
    &lt;dt-icon name=&quot;box-select&quot; size=&quot;100&quot; /&gt;
  &lt;/template&gt;
&lt;/dt-input&gt;
&lt;dt-input label=&quot;Extra large input with medium icon&quot; type=&quot;text&quot; placeholder=&quot;Placeholder&quot; :size=&quot;500&quot;&gt;
  &lt;template #startIcon&gt;
    &lt;dt-icon name=&quot;box-select&quot; size=&quot;200&quot; /&gt;
  &lt;/template&gt;
  &lt;template #endIcon&gt;
    &lt;dt-icon name=&quot;box-select&quot; size=&quot;200&quot; /&gt;
  &lt;/template&gt;
&lt;/dt-input&gt;
&lt;dt-input label=&quot;Medium textarea with large icon&quot; type=&quot;textarea&quot; placeholder=&quot;Placeholder&quot; :icon-size=&quot;300&quot; :size=&quot;400&quot;&gt;
  &lt;template #startIcon&gt;
    &lt;dt-icon name=&quot;box-select&quot; size=&quot;500&quot; /&gt;
  &lt;/template&gt;
&lt;/dt-input&gt;'>
<dt-stack data-demo-wrapper gap="200" class="d-w100p">
  <dt-input label="Medium input with smallest icon" type="text" placeholder="Placeholder" :size="300">
    <template #startIcon>
      <dt-icon name="box-select" size="100" />
    </template>
    <template #endIcon>
      <dt-icon name="box-select" size="100" />
    </template>
  </dt-input>
  <dt-input label="Extra large input with medium icon" type="text" placeholder="Placeholder" :size="500">
    <template #startIcon>
      <dt-icon name="box-select" size="200" />
    </template>
    <template #endIcon>
      <dt-icon name="box-select" size="200" />
    </template>
  </dt-input>
  <dt-input label="Medium textarea with large icon" type="textarea" placeholder="Placeholder" :icon-size="300" :size="400">
    <template #startIcon>
      <dt-icon name="box-select" size="500" />
    </template>
  </dt-input>
</dt-stack>
</code-example>
<h2 id="label-size" tabindex="-1" class="d-docsite--header-2">Label size <a class="header-anchor d-link d-docsite--link d-link" href="#label-size" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The label text size is automatically derived from the component's <code v-pre>size</code> prop. Use the <code v-pre>label-size</code> prop to override this when you need a different label size independent of the input size. For example, the default label size for a <code v-pre>:size=&quot;300&quot;</code> input is <code v-pre>300</code>, but you can override it from <code v-pre>100</code> to <code v-pre>400</code>.</p>
<code-example source-code='&lt;dt-input label=&quot;Extra small label&quot; placeholder=&quot;Placeholder&quot; :label-size=&quot;100&quot; /&gt;'>
<dt-stack data-demo-wrapper gap="200" class="d-w100p">
  <dt-input label="Extra small label" placeholder="Placeholder" :label-size="100" />
  <dt-input label="Small label" placeholder="Placeholder" :label-size="200" />
  <dt-input label="Medium label (default)" placeholder="Placeholder" :label-size="300" />
  <dt-input label="Large label" placeholder="Placeholder" :label-size="400" />
</dt-stack>
</code-example>
<h2 id="label-strength" tabindex="-1" class="d-docsite--header-2">Label strength <a class="header-anchor d-link d-docsite--link d-link" href="#label-strength" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Override the label font weight independently of the label size. Valid values are <code v-pre>bold</code>, <code v-pre>semibold</code>, <code v-pre>medium</code>, and <code v-pre>normal</code>.</p>
<code-example source-code='&lt;dt-input label=&quot;Label&quot; placeholder=&quot;Placeholder&quot; label-strength=&quot;bold|semibold|medium|normal&quot; /&gt;'>
<dt-stack data-demo-wrapper gap="200" class="d-w100p">
  <dt-input label="Bold label" placeholder="Placeholder" label-strength="bold" />
  <dt-input label="Semibold label" placeholder="Placeholder" label-strength="semibold" />
  <dt-input label="Medium label" placeholder="Placeholder" label-strength="medium" />
  <dt-input label="Normal label" placeholder="Placeholder" label-strength="normal" />
</dt-stack>
</code-example>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="input" /><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<component-class-table component-name="input"></component-class-table><h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Make sure the <code v-pre>label</code> <code v-pre>for</code> attribute match the input <code v-pre>id</code>.</li>
<li class="d-docsite--list-element">Avoiding removing <code v-pre>labels</code>. Labelled inputs are user-friendly.</li>
<li class="d-docsite--list-element">Avoid relying on placeholder text as a substitute for a <code v-pre>label</code>.</li>
<li class="d-docsite--list-element">If the input is a required field, use the <code v-pre>aria-required</code> property and use the validation message for input errors.</li>
<li class="d-docsite--list-element">Input with validation errors should have <code v-pre>aria-describedby</code> with the <code v-pre>id</code> of the validation message.</li>
<li class="d-docsite--list-element">Placeholder text should not include critical information. Use description text for any information that helps the user successfully interact with the input.</li>
</ul>
</div></template>


<script setup>
import { ref } from 'vue';

const inputValue = ref('');
const inputSearchValue = ref('Some text');

const messages = {
  warning: { "message": "Warning validation message", "type": "warning" },
  critical: { "message": "Critical validation message", "type": "critical" },
  positive: { "message": "Positive validation message", "type": "positive" },
};

const multipleMessages = [
  { message: 'This is the first message', type: 'critical' },
  { message: 'This is the second message', type: 'critical' },
  { message: 'This is the third message', type: 'critical' },
];

const validateData = {
  length: {
    description: 'Max 25 characters.',
    max: 25,
    warn: 15,
    limitMaxLength: false,
  }
};

const validationMessage = () => {
  const remainingCharacters = validateData.length.max - inputValue.value.length;

  if (remainingCharacters < 0) {
    return `${Math.abs(remainingCharacters)} characters over limit`;
  } else {
    return `${remainingCharacters} characters left`;
  }
};

const validate = () => {
  return {
    length: {
      ...validateData.length,
      message: validationMessage(),
    }
  };
};
</script>